/**
 * Individual PO Line row component for the PO Lines table.
 * Renders dynamic column cells based on visibility configuration.
 */

import {
  AlertCircle,
  Check,
  XCircle,
  Pause,
  Play,
  Edit,
  FileSearch,
  ExternalLink,
  GitBranch,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PurchaseOrderLine, ColumnConfig, NextActionResult } from './types';
import { getAcknowledgeStatusBadge, getProcessingStageBadge, getPriorityBadge, canReleaseHold } from './utils';
import type { User } from '../../../../lib/api';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { toast } from 'sonner';
import {
  parseProgress,
  statusBadgeClass,
  shortStatusLabel,
  shortJobNumber,
  relativeAge,
} from '../../jobStatusParsing';

interface POLineRowProps {
  line: PurchaseOrderLine;
  user: User | null;
  columns: ColumnConfig[];
  selectedLineIds: Set<number>;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  getNextAction: (line: PurchaseOrderLine) => NextActionResult;
  canSelectForBulkAccept: (line: PurchaseOrderLine) => boolean;
  onSelectLine: (lineId: number) => void;
  onReviewChanges: (lineId: number) => void;  // CR-4728
  onShowJobStatus: (lineId: number) => void;
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onToggleCritical: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  onToggleSelect: (lineId: number) => void;
  scheduleCollabEnabled?: boolean;
  // CR-4687 round 3: when ``true``, two or more rows are selected so per-line
  // action controls (Accept / Decline / ReviewChanges / Propose Change /
  // Put on hold / Release hold) are rendered disabled. Bulk actions are the
  // only allowed path in this mode — prevents a tester from acknowledging
  // one line while three are checked, which previously left the selection
  // count out of sync with the underlying state.
  bulkMode?: boolean;
}

export function POLineRow({
  line,
  user,
  columns,
  selectedLineIds,
  formatDate,
  formatCurrency,
  shouldShowActions,
  shouldShowDecline,
  shouldShowProposeChange,
  getNextAction,
  canSelectForBulkAccept,
  onSelectLine,
  onReviewChanges,
  onShowJobStatus,
  onSelectLineForWorkflow,
  onProposeChange,
  onToggleCritical: _onToggleCritical,
  onPutOnHold,
  onReleaseHold,
  onToggleSelect,
  scheduleCollabEnabled = false,
  bulkMode = false,
}: POLineRowProps) {
  const bulkModeTitle = 'Disabled — multiple lines selected. Use bulk actions or clear selection.';
  const nextAction = getNextAction(line);
  // Conv. columns are stored in GBP (platform base) — formatCurrencyFromBase
  // converts them into the user's preferred currency so customers see their
  // trading currency (e.g. Drewar -> PLN) instead of a hardcoded £ label.
  const { formatCurrencyFromBase } = useFormatters();

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'processing_stage':
        // DNA-driven surface-treatment stage (Docked/WIP/Completed/Shipped).
        // Auto-maintained from the DNA Status code on import. Holds come from
        // a separate DNA signal (OnHoldLogs -> on_hold flag), not the Status
        // code, so overlay "On Hold" when the flag is set — mirroring the
        // Ack Status cell — otherwise a held line would still read Docked/WIP.
        return (
          <td key={columnId} className="py-3 px-4">
            {line.on_hold
              ? getProcessingStageBadge('on_hold')
              : getProcessingStageBadge(line.processing_stage || '')}
          </td>
        );

      case 'acknowledge_status':
        return (
          <td key={columnId} className="py-3 px-4">
            {/* CR-4696: Show "Closed" for fully received lines */}
            {line.status === 'received' ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Closed</span>
            ) : line.on_hold ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500 text-white">On Hold</span>
            ) : (
              getAcknowledgeStatusBadge(line.acknowledge_status || '')
            )}
          </td>
        );

      case 'action': {
        const isSupplierUser = user?.platform === 'SUPPLIER' || user?.platform === 'HELIX';
        const hasChangeProposed = line.acknowledge_status === 'change_proposed_customer' || line.acknowledge_status === 'change_proposed_supplier';
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            <div className="flex items-center justify-center gap-2">
              {/* CR-4728: Supplier users see a single "Review Changes" icon for proposed changes.
                  Opens the PO Line popup directly on the Change Requests tab. */}
              {isSupplierUser && hasChangeProposed ? (
                <button onClick={(e) => { e.stopPropagation(); onReviewChanges(line.id); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-amber-50'}`} title={bulkMode ? bulkModeTitle : 'Review Changes'}>
                  <FileSearch className="h-5 w-5 text-amber-600" />
                </button>
              ) : (
                <>
                  {shouldShowActions(line) && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); onSelectLineForWorkflow(line, 'accept'); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-green-50'}`} title={bulkMode ? bulkModeTitle : 'Accept'}>
                        <Check className="h-5 w-5 text-green-600" />
                      </button>
                      {shouldShowDecline(line) && (
                        <button onClick={(e) => { e.stopPropagation(); onSelectLineForWorkflow(line, 'decline'); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50'}`} title={bulkMode ? bulkModeTitle : 'Decline'}>
                          <XCircle className="h-5 w-5 text-red-600" />
                        </button>
                      )}
                    </>
                  )}
                  {shouldShowProposeChange(line) && (
                    <button onClick={(e) => { e.stopPropagation(); onProposeChange(line); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`} title={bulkMode ? bulkModeTitle : 'Propose Change'}>
                      <Edit className="h-5 w-5 text-blue-600" />
                    </button>
                  )}
                  {!shouldShowActions(line) && !shouldShowProposeChange(line) && (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </>
              )}
            </div>
          </td>
        );
      }

      case 'is_critical':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.is_critical ? (
              <span className="p-1.5 rounded bg-amber-100 inline-flex" title="Critical">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </td>
        );

      case 'on_hold':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.on_hold ? (
              <button
                onClick={(e) => { e.stopPropagation(); if (canReleaseHold(user)) { onReleaseHold(line); } else { toast.error('You do not have permission to release this hold'); } }}
                className={`p-1.5 rounded transition-colors ${bulkMode ? 'bg-green-100 opacity-40 cursor-not-allowed' : 'bg-green-100 hover:bg-green-200'}`}
                title={bulkMode ? bulkModeTitle : 'Release from hold'} disabled={bulkMode || !canReleaseHold(user)}
              >
                <Play className="h-4 w-4 text-green-700" />
              </button>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); onPutOnHold(line); }} disabled={bulkMode} className={`p-1.5 rounded transition-colors ${bulkMode ? 'bg-orange-100 opacity-40 cursor-not-allowed' : 'bg-orange-100 hover:bg-orange-200'}`} title={bulkMode ? bulkModeTitle : 'Put on hold'}>
                <Pause className="h-4 w-4 text-orange-700" />
              </button>
            )}
          </td>
        );

      case 'hold_reason':
        return (
          <td key={columnId} className="py-3 px-4">
            {line.on_hold ? (
              <div className="text-sm">{line.on_hold_reason_text || line.on_hold_reason_details?.description || 'On hold'}</div>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </td>
        );

      case 'next_action':
        return (
          <td key={columnId} className="py-3 px-4">
            <div className="flex justify-center">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${nextAction.className}`}>{nextAction.text}</span>
            </div>
          </td>
        );

      case 'po_number':
        return (<td key={columnId} className="py-3 px-4"><span className="font-medium text-sm">{line.po_number || `PO-${line.po}`}</span></td>);

      case 'line_number':
        return (<td key={columnId} className="py-3 px-4 text-center"><span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{line.line_number}</span></td>);

      case 'customer_name':
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            {line.customer_id ? (
              <Link
                to={`/customers/${line.customer_id}`}
                className="text-primary hover:underline inline-flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {line.customer_name || 'N/A'}
                <ExternalLink className="h-3 w-3" />
              </Link>
            ) : (
              <span>{line.customer_name || 'N/A'}</span>
            )}
          </td>
        );

      case 'supplier_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.supplier_name || 'N/A'}</td>);
      case 'part_number':
        return (<td key={columnId} className="py-3 px-4"><span className="font-mono text-sm font-medium">{line.part_number}</span></td>);
      case 'description':
        return (<td key={columnId} className="py-3 px-4"><div className="text-sm">{line.description}</div></td>);
      case 'quantity':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{parseFloat(line.quantity).toFixed(0)}</span></td>);
      case 'received_qty':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{parseFloat(line.received_qty).toFixed(0)}</span></td>);
      case 'outstanding_qty':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className={`text-sm font-medium ${parseFloat(line.outstanding_qty) > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>{parseFloat(line.outstanding_qty).toFixed(0)}</span></td>);
      case 'pct_delivered': {
        const qty = parseFloat(line.quantity) || 0;
        const recv = parseFloat(line.received_qty) || 0;
        const pct = qty > 0 ? Math.min(Math.round((recv / qty) * 100), 100) : 0;
        const barColor = pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : pct > 0 ? 'bg-amber-500' : 'bg-gray-200';
        return (
          <td key={columnId} className="py-3 px-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" style={{ minWidth: '50px' }}>
                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
              </div>
              <span className={`text-xs font-medium tabular-nums w-10 text-right ${pct >= 100 ? 'text-emerald-600' : 'text-muted-foreground'}`}>{pct}%</span>
            </div>
          </td>
        );
      }
      case 'unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{formatCurrency(parseFloat(line.unit_price) || 0, line.currency, 2)}</td>);
      case 'total_price':
        return (<td key={columnId} className="py-3 px-4 text-right font-medium">{formatCurrency(parseFloat(line.total_price) || 0, line.currency, 2)}</td>);
      case 'delivery_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.delivery_date ? formatDate(line.delivery_date) : '-'}</td>);
      case 'supplier_promise_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.supplier_promise_date ? formatDate(line.supplier_promise_date) : ''}</td>);
      case 'buyer_id':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.buyer_id || '-'}</td>);
      case 'currency':
        return (<td key={columnId} className="py-3 px-4 text-sm text-center"><span className="px-2 py-1 bg-muted rounded text-xs font-medium">{line.currency || '-'}</span></td>);
      case 'batch_serial_no':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.batch_serial_no || '-'}</td>);
      case 'ppv':
        return (
          <td key={columnId} className="py-3 px-4 text-sm text-right">
            {line.ppv && parseFloat(line.ppv) !== 0 ? (
              <span className={`inline-block px-2 py-0.5 rounded text-white font-medium ${parseFloat(line.ppv) > 0 ? 'bg-red-500' : 'bg-amber-500'}`}>
                {formatCurrency(parseFloat(line.ppv), line.currency, true)}
              </span>
            ) : '-'}
          </td>
        );
      case 'otif_overridden':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.otif_overridden ? (<span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Yes</span>) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'poeton_job_number': {
        // CR-5260 — Poeton's internal ERP job numbers. A line can split into
        // several sub-jobs, so we show ALL distinct job numbers (from the
        // `job_numbers` aggregate). Fallbacks keep older payloads working: the
        // on-hold-log value, then the single latest progress narrative. The
        // on-hold-log source is empty for lines that never went on hold, which
        // is why the column previously showed '-'. Clicking opens the line's
        // Job Status tab in the modal, which breaks the jobs down in full.
        const jobs = (line.job_numbers && line.job_numbers.length)
          ? line.job_numbers
          : [line.poeton_job_number || parseProgress(line.latest_progress?.text).jobNumber || ''].filter(Boolean);
        if (!jobs.length) return <td key={columnId} className="py-3 px-4 text-sm font-mono text-muted-foreground">-</td>;
        const shown = jobs.map(shortJobNumber);
        const label = shown.length > 3 ? `${shown.slice(0, 3).join(', ')} +${shown.length - 3}` : shown.join(', ');
        // A split line ran as several sub-jobs, each with its own promise
        // date — the single date on the row hides that, so flag it here.
        // Count comes from the list annotation, not a per-row fetch.
        const splitCount = line.split_job_count || 0;
        const subJobs = line.job_count || jobs.length;
        return (
          <td key={columnId} className="py-3 px-4 text-sm font-mono">
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onShowJobStatus(line.id); }}
                className="text-primary hover:underline font-mono text-left"
                title={`${jobs.length} job${jobs.length === 1 ? '' : 's'}: ${jobs.join(', ')} — view Job Status`}
              >
                {label}
              </button>
              {splitCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onShowJobStatus(line.id); }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-sky-100 text-sky-800 hover:bg-sky-200 font-sans"
                  title={`Split across ${subJobs} sub-jobs, each with its own promise date — view Job Status`}
                >
                  <GitBranch className="h-3 w-3" />
                  Split &middot; {subJobs}
                </button>
              )}
            </span>
          </td>
        );
      }
      case 'job_status': {
        // Latest status pill + relative age. Job # / Qty live in their
        // own columns so the cell stays narrow and column-aligned.
        const text = (line.latest_progress?.text || '').trim();
        const at = line.latest_progress?.at || null;
        if (!text) return <td key={columnId} className="py-3 px-4 text-sm text-muted-foreground">—</td>;
        const p = parseProgress(text);
        return (
          <td key={columnId} className="py-3 px-4" title={text}>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadgeClass(p.statusText)}`}>
                {shortStatusLabel(p.statusText)}
              </span>
              {at && (
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {relativeAge(at)}
                </span>
              )}
            </span>
          </td>
        );
      }
      case 'job_no_active': {
        const text = (line.latest_progress?.text || '').trim();
        if (!text) return <td key={columnId} className="py-3 px-4 text-sm text-muted-foreground font-mono">—</td>;
        const p = parseProgress(text);
        if (!p.jobNumber) return <td key={columnId} className="py-3 px-4 text-sm text-muted-foreground font-mono">—</td>;
        return (
          <td key={columnId} className="py-3 px-4 text-sm font-mono">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onShowJobStatus(line.id); }}
              className="text-primary hover:underline font-mono"
              title={`View job status for ${p.jobNumber}`}
            >
              {shortJobNumber(p.jobNumber)}
            </button>
          </td>
        );
      }
      case 'job_qty_active': {
        const text = (line.latest_progress?.text || '').trim();
        if (!text) return <td key={columnId} className="py-3 px-4 text-sm text-muted-foreground text-right">—</td>;
        const p = parseProgress(text);
        return (
          <td key={columnId} className="py-3 px-4 text-sm text-right tabular-nums font-mono">
            {p.qty || <span className="text-muted-foreground">—</span>}
          </td>
        );
      }
      case 'otd_overridden':
        // CR-5263 — show forced verdict when overridden so the value is
        // legible at a glance instead of just a Yes/No flag.
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.otd_overridden ? (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${line.otd_override_value ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                {line.otd_override_value ? 'Forced ON-TIME' : 'Forced LATE'}
              </span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'customer_group_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.customer_group_name || '-'}</td>);
      case 'packing_note_number':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.packing_note_number || '-'}</td>);
      case 'order_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.order_date ? formatDate(line.order_date) : '-'}</td>);
      case 'last_received_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.last_received_date ? formatDate(line.last_received_date) : '-'}</td>);
      case 'updated_at':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.updated_at ? formatDate(line.updated_at, { includeTime: true }) : '-'}</td>);
      case 'required_date_change_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.required_date_change_count !== undefined && line.required_date_change_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{line.required_date_change_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'promised_date_change_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.promised_date_change_count !== undefined && line.promised_date_change_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-800 text-xs font-medium">{line.promised_date_change_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'pdf_attached_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.pdf_attached_count !== undefined && line.pdf_attached_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs font-medium">{line.pdf_attached_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'material_cert_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.material_cert_count !== undefined && line.material_cert_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">{line.material_cert_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'contract_unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.contract_unit_price ? formatCurrency(parseFloat(line.contract_unit_price), line.currency, 3) : '-'}</td>);
      case 'contract_lot_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.contract_lot_price ? formatCurrency(parseFloat(line.contract_lot_price), line.currency, 3) : '-'}</td>);
      case 'drawing_number':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.drawing_number || '-'}</td>);
      case 'drawing_version':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.drawing_version || '-'}</td>);
      case 'outstanding_value':
        return (
          <td key={columnId} className="py-3 px-4 text-right text-sm">
            {line.outstanding_value ? (<span className={parseFloat(line.outstanding_value) > 0 ? 'text-orange-600 font-medium' : ''}>{formatCurrency(parseFloat(line.outstanding_value), line.currency, 3)}</span>) : '-'}
          </td>
        );
      case 'converted_unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.converted_unit_price ? formatCurrencyFromBase(parseFloat(line.converted_unit_price)) : '-'}</td>);
      case 'converted_total_value':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm font-medium">{line.converted_total_value ? formatCurrencyFromBase(parseFloat(line.converted_total_value)) : '-'}</td>);
      case 'converted_outstanding_value':
        return (
          <td key={columnId} className="py-3 px-4 text-right text-sm">
            {line.converted_outstanding_value ? (<span className={parseFloat(line.converted_outstanding_value) > 0 ? 'text-orange-600 font-medium' : ''}>{formatCurrencyFromBase(parseFloat(line.converted_outstanding_value))}</span>) : '-'}
          </td>
        );
      case 'original_promise_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.original_promise_date ? formatDate(line.original_promise_date) : '-'}</td>);
      case 'next_job_due':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.next_job_due ? formatDate(line.next_job_due) : '-'}</td>);
      case 'mrp_scheduled_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.mrp_scheduled_date ? formatDate(line.mrp_scheduled_date) : '-'}</td>);
      case 'priority':
        return (<td key={columnId} className="py-3 px-4 text-center">{getPriorityBadge(line.priority)}</td>);
      case 'contract_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.contract_price ? formatCurrency(parseFloat(line.contract_price), line.currency) : '-'}</td>);
      case 'exchange_rate':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm font-mono">{line.exchange_rate ? parseFloat(line.exchange_rate).toFixed(6) : '-'}</td>);
      default:
        return null;
    }
  };

  const visibleColumns = columns.filter(col => col.visible);

  return (
    <tr
      className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${line.status === 'received' || line.status === 'cancelled' || line.acknowledge_status === 'closed' ? 'opacity-50 bg-muted/30' : ''} ${line.is_forecast ? 'bg-amber-50/50' : ''}`}
      title={line.is_forecast ? `Forecast — outside firm window (${line.firm_days ?? '?'} days)` : undefined}
      onClick={() => onSelectLine(line.id)}
    >
      {/* Per-row select checkbox hidden product-wide on supplier-portal
          (collaboration features off per the bulk-action-bar gate above). */}
      {false && !scheduleCollabEnabled && (
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canSelectForBulkAccept(line) ? (
            <input type="checkbox" checked={selectedLineIds.has(line.id)} onChange={() => onToggleSelect(line.id)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>
      )}
      {visibleColumns.map(col => renderCell(col.id))}
      {/* Supplier-portal: 'View →' link removed — the entire row is already
          clickable via onSelectLine, and the trailing column offset the table. */}
    </tr>
  );
}
